import { pipe } from "fp-ts/function";
import * as O from "fp-ts/Option";
import { compose } from "../../../data/function";
import { curry, uncurry } from "../../../function/curry";
import * as RA from "fp-ts/ReadonlyArray";
export * from "./compose";
export * from "./extra/iterable";
export * from "./lift";
export * from "./helpers";
export const fold = (foldr) => ({
    foldr,
});
export const of = (a) => fold((fold, empty, _s) => fold(a, empty));
export const empty = () => fold((_fold, empty, _s) => empty);
export const map = (g, f) => fold((fold, empty, s) => f.foldr((a, b) => fold(g(a), b), empty, s));
export const mapF = (g) => fold((fold, empty, s) => fold(g(s), empty));
export const chain = (g, f) => fold((fold, empty, s) => f.foldr((a1, b1) => g(a1).foldr((a2, b2) => fold(a2, b2), b1, s), empty, s));
export const chainF = (g) => fold((fold, empty, s) => g(s).foldr((a, b) => fold(a, b), empty, s));
export const ap = (f1, f2) => chain((a) => chain((f) => of(f(a)), f1), f2);
/**
 * Concat optics. Used for creating a list of optics where every optic gets
 * applied to the value. All results gets concatenated and can be shown with
 * `Fold.toList`. If `priority queue` of optics is needed, please use `Alt`
 *
 * example:
 *
 * ```typescript
 * const data = { x: "hello", y: "goodbye" };
 *
 * // dataM will be of `Maybe<"hello">`
 * const dataM = Fold.preview(
 *   Fold.concat(prop("x"), prop("y")),
 *   data
 * );
 *
 * // dataL will be of `["hello", "goodbye"]`
 * const dataL = Fold.toList(
 *   Fold.concat(prop("x"), prop("y")),
 *   data
 * );
 * ```
 */
export const concat = (...fs) => fold((fold, empty, s) => fs.reduceRight((b, f) => RA.reduceRight(b, fold)(toArray(f, s)), empty));
/**
 * Alternate optic. Follows `alt` laws and behaves as such. Used for creating a
 * list of optics where priority will be in order. Will not apply any optics
 * after an optics that has yielded a value. This is on contrast to `concat`
 * that will go through the whole list of optics and execute each one of them
 * concatinating them into a list.
 *
 * example:
 *
 * ```typescript
 * const data = { x: "hello", y: "goodbye" };
 *
 * // dataM will be of `Maybe<"hello">`
 * const dataM = Fold.preview(
 *   Fold.alt(prop("x"), prop("y")),
 *   data
 * );
 *
 * // dataL will be of `["hello"]`
 * const dataL = Fold.toList(
 *   Fold.alt(prop("x"), prop("y")),
 *   data
 * );
 * ```
 */
export const alt = (...fs) => fold((fold, empty, s) => {
    for (const f of fs) {
        const val = toArray(f, s);
        if (RA.isEmpty(val) === false) {
            return RA.reduceRight(empty, fold)(val);
        }
    }
    return empty;
});
export const has = (f, a, s) => pipe(toArray(f, s), RA.findIndex((x) => x === a), O.isSome);
// pre has a larger definition. As we dont have IndexedPreservingGetter right now, we'll ignore that for now
// https://hackage.haskell.org/package/lens-4.17.1/docs/Control-Lens-Fold.html
// FIXME: Put this as a IndexedPreservingGetter whenever we implement it
// pre :: Getting (First a) s a -> IndexPreservingGetter s (Maybe a)
export const pre = (f) => fold((fold, empty, s) => fold(f.foldr(O.fromNullable, O.none, s), empty));
export function pre_(F, M) {
    return (f) => fold((fold, empty, s) => fold(f.foldr((a, b) => M.concat(F.of(a), b), M.empty, s), empty));
}
export const view_ = (M) => (f, s) => f.foldr(M.concat, M.empty, s);
export function preview_(F, M) {
    return (f, g) => pipe(pre_(F, M)(f), (ff) => view_(M)(ff, g));
}
export function toArray(f, s) {
    return preview_(RA.Applicative, RA.getMonoid())(f, s);
}
export const view = (f, s) => f.foldr((a, b) => O.alt(() => b)(a), O.none, s);
// FIXME: Typescript wont follow here if we inline curry
const curriedView = curry(view);
export const preview = uncurry(compose(pre, curriedView));

